import React from 'react';
import { Link as RouterLink } from 'react-router';
import { styled, Theme } from '@mui/material/styles';
import clsx from 'clsx';
import { radius } from '../theme/tokens.radius';

type UIKitLinkKind = 'internal' | 'external' | 'button';

interface Props {
  htmlId?: string;
  className?: string;
  icon?: React.ReactNode;
  text?: string;
  onClick?: () => void;
  visited?: boolean;
  /** si es true, ocupa todo el ancho que le pases en width o 100% */
  noFit?: boolean;
  /** si es true, texto siempre subrayado; si es false, sólo hover */
  underline?: boolean;
  title?: string;
  width?: string;
  /** estilo del UIKit: default (brand) o subtle (neutral) */
  styleType?: 'default' | 'subtle';
  /** tamaño tipográfico: large/body1, medium/body2, small/caption */
  size?: 'large' | 'medium' | 'small';

  /* tipo explícito de link (si no lo ponés, se infiere) */
  kind?: UIKitLinkKind;
  /* ruta interna (SPA) */
  to?: string;
  /* URL externa */
  href?: string;
  /* target para links externos */
  target?: '_blank' | '_self';
  /* rel para links externos */
  rel?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

// JSS permitía "&:hover $textCommon" para que el hover del link (root)
// subraye el <span> de texto hijo, sin necesitar :hover sobre el span en
// sí. Con styled() se usa un nombre de clase fijo (no generado por
// emotion) como hook estable para ese mismo descendant selector - mismo
// patrón que ButtonToggle.tsx/Input.tsx.
const LINK_TEXT_CLASS = 'uikit-link-text';

interface LinkStyleProps {
  styleType: 'default' | 'subtle';
  visited?: boolean;
  disabled?: boolean;
}

const linkStyle = (theme: Theme, { styleType, visited, disabled }: LinkStyleProps) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px 4px',
  borderRadius: radius.s,
  border: 'none',
  textDecoration: 'none',
  cursor: 'pointer',
  outline: 'none',
  background: 'transparent',
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? ('none' as const) : ('auto' as const),
  // underline en hover (para cualquier size)
  [`&:hover .${LINK_TEXT_CLASS}`]: {
    textDecoration: 'underline !important',
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
  },
  ...(styleType === 'subtle'
    ? {
        color: theme.palette.text.secondary,
        '&&:hover': { color: theme.palette.text.secondary },
        '&&:focus-visible': {
          color: theme.palette.text.secondary,
          boxShadow: `0 0 0 2px ${theme.palette.grey[300]}`,
        },
      }
    : {
        color: theme.palette.primary.main,
        '&&:hover': { color: theme.palette.primary.main },
        '&&:focus-visible': { color: theme.palette.primary.main },
      }),
  ...(visited && { color: theme.palette.primary.dark }),
});

const shouldForwardLinkProp = (prop: string) =>
  prop !== 'styleType' && prop !== 'visited' && prop !== 'disabled';

// El elemento real a renderizar (RouterLink/<a>/<button>) se elige en
// runtime - se pre-crean los 3 styled() posibles acá afuera del render (no
// se puede llamar styled() DENTRO del componente, remontaría el DOM en
// cada render) y se elige cuál usar más abajo.
const StyledRouterLink = styled(RouterLink, {
  shouldForwardProp: shouldForwardLinkProp,
})<LinkStyleProps>(({ theme, ...props }) => linkStyle(theme, props));
const StyledAnchor = styled('a', { shouldForwardProp: shouldForwardLinkProp })<LinkStyleProps>(
  ({ theme, ...props }) => linkStyle(theme, props)
);
const StyledButtonEl = styled('button', {
  shouldForwardProp: shouldForwardLinkProp,
})<LinkStyleProps>(({ theme, ...props }) => linkStyle(theme, props));

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'noFit' && prop !== 'uiWidth',
})<{ noFit?: boolean; uiWidth?: string }>(({ noFit, uiWidth }) => ({
  width: noFit ? uiWidth || '100%' : 'fit-content',
  display: 'inline-block',
}));

const Icon = styled('span')(({ theme }) => ({
  marginRight: theme.spacing(1),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const Text = styled('span', {
  shouldForwardProp: (prop) => prop !== 'uiSize' && prop !== 'underline',
})<{ uiSize: 'large' | 'medium' | 'small'; underline?: boolean }>(
  ({ theme, uiSize, underline }) => {
    const getTypography = () => {
      if (uiSize === 'large') {
        return theme.typography.body1;
      }
      if (uiSize === 'small') {
        return theme.typography.caption;
      }
      return theme.typography.body2;
    };

    return {
      ...getTypography(),
      ...(underline && {
        '&, & *': {
          textDecoration: 'underline !important',
        },
      }),
    };
  }
);

const UIKitLink: React.FC<Props> = (props) => {
  const {
    htmlId,
    className,
    icon,
    text = '',
    onClick,
    visited = false,
    underline = false,
    title,
    styleType = 'default',
    size = 'medium',
    kind,
    to,
    href,
    target,
    rel,
    style,
    disabled,
    noFit,
    width,
  } = props;

  // Inferimos el tipo si no viene explícito
  let resolvedKind: UIKitLinkKind = 'button';
  if (kind) {
    resolvedKind = kind;
  } else if (to) {
    resolvedKind = 'internal';
  } else if (href) {
    resolvedKind = 'external';
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (resolvedKind === 'button') {
      // modo botón: no navegamos nunca
      e.preventDefault();
      if (onClick) {
        onClick();
      }
      return;
    }

    // internal / external
    if (onClick) {
      // si hay onClick, dejamos que decida si quiere prevenir o no
      onClick();
    }
    // si NO hay onClick y es internal/external, dejamos que navegue normal
  };

  const commonProps = {
    style,
    id: htmlId,
    title,
    onClick: handleClick,
    className,
    disabled,
    styleType,
    visited,
  };

  const textClassName = clsx(LINK_TEXT_CLASS);

  const content = (
    <>
      {icon && <Icon>{icon}</Icon>}
      <Text uiSize={size} underline={underline} className={textClassName}>
        {text}
      </Text>
    </>
  );

  let element: React.ReactNode;
  if (resolvedKind === 'internal' && to) {
    element = (
      <StyledRouterLink {...commonProps} to={to}>
        {content}
      </StyledRouterLink>
    );
  } else if (resolvedKind === 'external' && href) {
    element = (
      <StyledAnchor {...commonProps} href={href} target={target} rel={rel}>
        {content}
      </StyledAnchor>
    );
  } else {
    element = (
      <StyledButtonEl {...commonProps} type="button">
        {content}
      </StyledButtonEl>
    );
  }

  return (
    <Wrapper noFit={noFit} uiWidth={width}>
      {element}
    </Wrapper>
  );
};

export default UIKitLink;
